import { Component } from '@theme/component';

/**
 * Swaps between the two store groups and narrows the visible one by city.
 *
 * It never fetches and never rewrites the grid: both groups and every card are
 * already on the page, so the whole job is toggling `hidden`. With the script
 * blocked the page still works -- both groups visible, nothing filtered -- and
 * every store keeps its link to its own page.
 *
 * @extends {Component}
 */
class MmStoreLocator extends Component {
  // No `requiredRefs`: a store with no entries yet renders the component with
  // no panels at all, and that is a quiet empty state, not a broken component.

  connectedCallback() {
    super.connectedCallback();
    this.#syncTabStops();
    this.#apply();
  }

  /**
   * Fires only when this component's own subtree changed (component.js:91), so
   * it covers the theme editor re-rendering the block. `super` is what
   * refreshes `refs`; skipping it leaves them pointing at detached nodes.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#syncTabStops();
    this.#apply();
  }

  /**
   * A tab. The city is reset rather than carried across, because a city that
   * exists in one group need not exist in the other and a select showing
   * «Guadalajara» over an empty grid reads as broken.
   *
   * @param {Event} event
   */
  pickTab(event) {
    const tab = event.target instanceof Element ? event.target.closest('.mm-store-locator__tab') : null;
    if (!tab) return;

    const group = tab.dataset.mmGroup;
    if (!group || group === this.#group()) return;

    for (const candidate of this.#tabs()) {
      candidate.setAttribute('aria-selected', candidate === tab ? 'true' : 'false');
    }

    const { filter } = this.refs;
    if (filter instanceof HTMLSelectElement) filter.value = '';

    this.#syncTabStops();
    this.#apply();
  }

  /**
   * Left/Right move between tabs and Home/End jump to the ends, which is what
   * a `role="tablist"` is expected to do once a tab has focus.
   *
   * @param {KeyboardEvent} event
   */
  moveTab(event) {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    const tabs = this.#tabs();
    if (tabs.length < 2) return;

    const current = tabs.indexOf(/** @type {HTMLElement} */ (event.target));
    if (current === -1) return;

    event.preventDefault();

    let next = current;
    if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
    if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;

    tabs[next]?.focus();
    tabs[next]?.click();
  }

  /** The city select. */
  pickCity() {
    this.#apply();
  }

  /** Shows the active group, hides the rest, and narrows it by city. */
  #apply() {
    const group = this.#group();
    const city = this.refs.filter instanceof HTMLSelectElement ? this.refs.filter.value : '';

    let matches = 0;

    for (const panel of this.#panels()) {
      const active = panel.dataset.mmGroup === group;
      panel.hidden = !active;
      if (!active) continue;

      for (const item of panel.querySelectorAll('.mm-store-locator__item')) {
        const hit = city === '' || item.getAttribute('data-mm-city') === city;
        item.toggleAttribute('hidden', !hit);
        if (hit) matches += 1;
      }
    }

    this.#report(matches, city);
  }

  /**
   * The empty line and the live region. The count is announced and not printed:
   * the design draws no counter, and a number appearing where none was drawn is
   * a decision nobody made.
   *
   * @param {number} matches
   * @param {string} city
   */
  #report(matches, city) {
    const { empty, status } = this.refs;

    if (empty instanceof HTMLElement) {
      const label = this.#cityLabel(city);
      empty.hidden = matches > 0;
      empty.textContent = matches > 0 ? '' : (this.dataset.mmEmpty ?? '').replace('%%CITY%%', label);
    }

    if (status instanceof HTMLElement) {
      // Silent until something is actually filtered: announcing the resting
      // grid on every connect would talk over the page load.
      if (city === '') {
        status.textContent = '';
        return;
      }

      const template = matches === 1 ? (this.dataset.mmCountOne ?? '') : (this.dataset.mmCountOther ?? '');
      status.textContent = template.replace('%%COUNT%%', String(matches));
    }
  }

  /**
   * The label as the visitor read it, not the handle we match on.
   *
   * @param {string} city
   * @returns {string}
   */
  #cityLabel(city) {
    const { filter } = this.refs;
    if (!(filter instanceof HTMLSelectElement)) return city;
    return filter.querySelector(`option[value="${CSS.escape(city)}"]`)?.textContent?.trim() ?? city;
  }

  /** Only the selected tab is a tab stop; the arrows reach the others. */
  #syncTabStops() {
    for (const tab of this.#tabs()) {
      tab.tabIndex = tab.getAttribute('aria-selected') === 'true' ? 0 : -1;
    }
  }

  /** @returns {string} The group the selected tab points at, '1' when there are no tabs. */
  #group() {
    const selected = this.#tabs().find((tab) => tab.getAttribute('aria-selected') === 'true');
    return selected?.dataset.mmGroup ?? this.#panels()[0]?.dataset.mmGroup ?? '1';
  }

  /** @returns {HTMLElement[]} */
  #tabs() {
    const { tabs } = this.refs;
    if (!tabs) return [];
    return (Array.isArray(tabs) ? tabs : [tabs]).filter(
      /** @returns {element is HTMLElement} */ (element) => element instanceof HTMLElement
    );
  }

  /** @returns {HTMLElement[]} */
  #panels() {
    const { panels } = this.refs;
    if (!panels) return [];
    return (Array.isArray(panels) ? panels : [panels]).filter(
      /** @returns {element is HTMLElement} */ (element) => element instanceof HTMLElement
    );
  }
}

if (!customElements.get('mm-store-locator')) {
  customElements.define('mm-store-locator', MmStoreLocator);
}
